<template>
  <button
    class="ln-btn ln-btn-success ln-btn-xs"
    :title="computedTitle"
    @click="onExport"
  >
    <font-awesome-icon :icon="['fas','table']" aria-hidden="true" />
    <span class="sr-only">Export to Excel</span>
    <span class="ln-btn-text">{{ label }}</span>
  </button>
</template>

<script>
/**
 * @component ExportExcelButton
 * @desc Universal Excel export button. It tries to auto-detect common table sources.
 * - vue-good-table: pass its ref via `tableRef` or container via `selector`.
 * - Native HTML table: pass CSS `selector` (e.g., '#salesTable').
 * - Direct data: pass `sheets` or `rows` props.
 * - Multi-export: pass array of sheet specs in `sheets`.
 */
import { exportExcel, htmlTableToObjects } from '@/Services/exportExcel';

export default {
  name: 'ExportExcelButton',
  props: {
    label: { type: String, default: 'Excel' },
    filename: { type: String, default: '' },
    // Provide data directly
    rows: { type: Array, default: null },
    sheets: { type: Array, default: null }, // [{ name, rows, columns }]
    columns: { type: Array, default: null },
    // For vue-good-table: provide the component ref name
    tableRef: { type: String, default: '' },
    // CSS selector to a container or <table>
    selector: { type: String, default: '' },
    // Optional: multiple CSS selectors to export as multiple sheets
    selectors: { type: Array, default: () => [] },
    // Optional sheet name when rows-only
    sheetName: { type: String, default: 'Data' }
  },
  computed: {
    computedTitle() {
      const name = this.filename || this.defaultFileName();
      return `Export to Excel${name ? `: ${name}` : ''}`;
    }
  },
  methods: {
    defaultFileName() {
      try {
        const base = (this.label && this.label.trim()) || 'Table Export';
        const route = (this.$route && this.$route.name) ? ` - ${this.$route.name}` : '';
        const tf = (this.$store && this.$store.getters && this.$store.getters.getTimeFrame) || {};
        const storeName = (this.$store && this.$store.getters && this.$store.getters.getStore && this.$store.getters.getStore.StoreName) || 'All Stores';
        const helper = (window.buildExportName || ((b, ctx) => {
          const pad = (n) => String(n).padStart(2, '0');
          const dt = new Date();
          const stamp = `${dt.getFullYear()}${pad(dt.getMonth()+1)}${pad(dt.getDate())}-${pad(dt.getHours())}${pad(dt.getMinutes())}${pad(dt.getSeconds())}`;
          const safe = (s) => (s||'').toString().replace(/[\\/:*?"<>|]+/g, ' ').trim();
          const rng = (ctx.from && ctx.to) ? `${safe(ctx.from)}_to_${safe(ctx.to)}` : stamp;
          return `${safe(b)} - ${safe(ctx.storeLabel||'All Stores')}${route ? ` - ${safe(route.replace(/\s+/g,' ').trim())}` : ''} - ${rng} - ${stamp}`.slice(0,180);
        }));
        return helper(base, { storeLabel: storeName, from: tf.from || tf.startTime, to: tf.to || tf.endTime });
      } catch (e) {
        const dt = new Date();
        return `export_${dt.getFullYear()}${String(dt.getMonth()+1).padStart(2,'0')}${String(dt.getDate()).padStart(2,'0')}-${String(dt.getHours()).padStart(2,'0')}${String(dt.getMinutes()).padStart(2,'0')}${String(dt.getSeconds()).padStart(2,'0')}`;
      }
    },
    normalizeSheetsForExport(data) {
      const arr = Array.isArray(data) ? data : (data ? [data] : []);
      return arr.map((sheet, idx) => ({
        name: (sheet && sheet.name) || this.sheetName || `Sheet_${idx + 1}`,
        rows: (sheet && sheet.rows) || [],
        columns: (sheet && sheet._columns && sheet._columns.length) ? sheet._columns : (sheet && sheet.columns)
      }));
    },
    onExport() {
      const effectiveName = this.filename || this.defaultFileName();
      // 1) Direct sheets override all
      if (this.sheets && this.sheets.length) {
        const sheets = this.normalizeSheetsForExport(this.sheets);
        exportExcel(sheets, { filename: effectiveName });
        return;
      }
      // 2) Direct rows with optional columns
      if (this.rows && this.rows.length) {
        exportExcel([{ name: this.sheetName, rows: this.rows, columns: this.columns }], { filename: effectiveName });
        return;
      }
      // 3) Multiple selectors -> multiple sheets
      if (this.selectors && this.selectors.length) {
        const all = this.selectors.map((sel, i) => {
          const el = document.querySelector(sel);
          const { rows, labels } = this.extractFromContainer(el);
          return { name: this.guessNameFromSelector(sel, i), rows, _columns: labels };
        });
        const sheets = this.normalizeSheetsForExport(all);
        const prepared = sheets.length ? sheets : [{ name: this.sheetName || 'Data', rows: [] }];
        const hasRows = prepared.some(s => Array.isArray(s.rows) && s.rows.length);
        exportExcel(hasRows ? prepared : [{ name: 'Data', rows: [] }], { filename: effectiveName });
        return;
      }
      // 4) Single selector or tableRef or fallback auto-detect
      const data = this.autoDetectData();
      const sheets = this.normalizeSheetsForExport(data);
      const prepared = sheets.length ? sheets : [{ name: this.sheetName || 'Data', rows: [] }];
      const hasRows = prepared.some(s => Array.isArray(s.rows) && s.rows.length);
      exportExcel(hasRows ? prepared : [{ name: 'Data', rows: [] }], { filename: effectiveName });
    },

    autoDetectData() {
      // vue-good-table via ref
      if (this.tableRef && this.$root.$refs && this.$root.$refs[this.tableRef]) {
        const comp = this.$root.$refs[this.tableRef];
        const vgt = comp && comp.$children && comp.$children.find(c => c.$options && (c.$options._componentTag || c.$options.name || '').toString().includes('vue-good-table'));
        const { rows, labels } = this.extractFromVueGoodTable(comp || vgt);
        return [{ name: this.sheetName || 'Table', rows, _columns: labels }];
      }
      // Container or table by selector
      if (this.selector) {
        const el = document.querySelector(this.selector);
        const { rows, labels } = this.extractFromContainer(el);
        return [{ name: this.guessNameFromSelector(this.selector, 0), rows, _columns: labels }];
      }
      // Global auto: find first table in nearest view
      const nearestTable = this.$el && this.$el.closest && this.$el.closest('[data-export-scope]')?.querySelector('table') || document.querySelector('main table, .view table, table');
      if (nearestTable) {
        const { rows, labels } = this.extractFromContainer(nearestTable);
        return [{ name: 'Table', rows, _columns: labels }];
      }
      return [{ name: 'Data', rows: [], _columns: [] }];
    },

    pickFieldValue(row, field) {
      if (!field || row == null) return row && row[field];
      if (typeof field === 'function') {
        try {
          return field(row);
        } catch (_) {
          return undefined;
        }
      }
      if (typeof field === 'string' && field.includes('.')) {
        return field.split('.').reduce((acc, key) => (acc != null ? acc[key] : undefined), row);
      }
      return row[field];
    },

    findVueGoodTableInstance(el) {
      if (!el) return null;
      const candidates = [];
      const root = el.closest ? el.closest('.vgt-wrap') : null;
      if (root && root.__vue__) candidates.push(root.__vue__);
      let node = el;
      while (node) {
        if (node.__vue__ && !candidates.includes(node.__vue__)) {
          candidates.push(node.__vue__);
        }
        node = node.parentElement;
      }
      for (const vm of candidates) {
        const found = this.findVueGoodTableInVm(vm, el);
        if (found) return found;
      }
      return null;
    },

    findVueGoodTableInVm(vm, el) {
      if (!vm || !vm.$el || (el && !vm.$el.contains(el))) return null;
      const name = ((vm.$options && (vm.$options.name || vm.$options._componentTag)) || '').toString().toLowerCase();
      if (name.includes('vue-good-table')) return vm;
      for (const child of vm.$children || []) {
        if (child.$el && child.$el.contains(el)) {
          const match = this.findVueGoodTableInVm(child, el);
          if (match) return match;
        }
      }
      return null;
    },

    extractFromVueGoodTable(vgtComp) {
      try {
        if (!vgtComp) return { rows: [], labels: [] };
        // Prefer prop columns; fallback to instance .columns
        const propCols = (vgtComp.$props && (vgtComp.$props.columns || vgtComp.$props.cols)) || vgtComp.columns || [];
        // Normalize to { field, label }
        const cols = (propCols || []).map(c => ({
          field: c.field || c.key || c.label,
          label: c.label || c.field || c.key || ''
        })).filter(c => c.field);

        // Rows: prefer processedRows/computedRows then original, flatten children to include all pages
        const flattenRows = (list) => {
          const acc = [];
          (list || []).forEach(hr => {
            const kids = (hr && hr.children) || [];
            kids.forEach(row => acc.push(row.original || row));
          });
          return acc;
        };
        let rawRows = [];
        if (Array.isArray(vgtComp.processedRows) && vgtComp.processedRows.length) {
          rawRows = flattenRows(vgtComp.processedRows);
        } else if (Array.isArray(vgtComp.filteredRows) && vgtComp.filteredRows.length) {
          rawRows = flattenRows(vgtComp.filteredRows);
        } else if (Array.isArray(vgtComp.originalRows) && vgtComp.originalRows.length) {
          rawRows = flattenRows(vgtComp.originalRows);
        }
        if (!rawRows.length && vgtComp.$props && Array.isArray(vgtComp.$props.rows)) {
          rawRows = vgtComp.$props.rows.slice();
        }

        // Build Excel rows keyed by labels but picking from fields
        const rows = rawRows.map(r => cols.reduce((o, c) => {
          o[c.label] = this.pickFieldValue(r, c.field);
          return o;
        }, {}));

        const labels = cols.map(c => c.label);
        return { rows, labels };
      } catch (e) {
        return { rows: [], labels: [] };
      }
    },

    extractFromContainer(el) {
      if (!el) return { rows: [], labels: [] };

      // Try to resolve vue-good-table instance from DOM to pull full dataset
      const vgtInstance = this.findVueGoodTableInstance(el);
      if (vgtInstance) {
        return this.extractFromVueGoodTable(vgtInstance);
      }

      // If it’s a native table element
      if (el.tagName && el.tagName.toLowerCase() === 'table') {
        const rows = htmlTableToObjects(el);
        return { rows, labels: rows.length ? Object.keys(rows[0]) : [] };
      }
      // If it hosts vue-good-table
      if (el.querySelector) {
        const vgtTable = el.querySelector('.vue-good-table table, .vgt-table, table');
        if (vgtTable && vgtTable.tagName && vgtTable.tagName.toLowerCase() === 'table') {
          const rows = htmlTableToObjects(vgtTable);
          return { rows, labels: rows.length ? Object.keys(rows[0]) : [] };
        }
      }
      // Fallback: attempt to read list items with data attributes
      if (el.querySelector) {
        const items = Array.from(el.querySelectorAll('[data-export-item]'));
        if (items.length) {
          const rows = items.map(node => {
            const obj = {};
            Array.from(node.attributes).forEach(a => {
              if (a.name.startsWith('data-col-')) {
                obj[a.name.replace('data-col-','')] = a.value;
              }
            });
            if (Object.keys(obj).length === 0) obj.value = node.textContent.trim();
            return obj;
          });
          return { rows, labels: rows.length ? Object.keys(rows[0]) : [] };
        }
      }
      return { rows: [], labels: [] };
    },

    guessNameFromSelector(sel, index) {
      const id = sel.replace(/[#.\s>]+/g, '_').replace(/^_+|_+$/g, '') || `Sheet_${index + 1}`;
      return id.slice(0, 31);
    },
  }
};
</script>

<style scoped>
/* L⚡t‑ning tokens, no hardcoded colors; radius ≤ 5px per project cap */
.ln-btn {
  --h: 28px;
  --icon-size: 16px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: var(--h);
  padding: 0 12px;
  font-family: var(--font-family, Montserrat, sans-serif);
  font-weight: 500;
  font-size: 14px;
  color: var(--text-primary);
  background: var(--accent-primary);
  border: 1px solid transparent;
  border-radius: var(--radius-button, 5px);
  transition: 0.3s ease all;
}
.ln-btn:hover { filter: brightness(1.05); }
.ln-btn:focus { outline: 2px solid var(--accent-secondary); outline-offset: 2px; }
.ln-btn-primary { background: var(--accent-primary); color: #fff; border-color: transparent; }
.ln-btn-success { background: var(--success); color: #fff; border-color: transparent; }
.ln-btn-xs { --h: 28px; padding: 0 10px; }
.ln-btn-text { line-height: 1; }
/* Ensure icons fit comfortably inside the 28px button height */
.ln-btn svg { width: var(--icon-size); height: var(--icon-size); flex: 0 0 auto; display: inline-block; }
.sr-only { position: absolute; width: 1px; height: 1px; padding: 0; overflow: hidden; clip: rect(0, 0, 0, 0); white-space: nowrap; border: 0; }
</style>
